import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import Lightbox from '../components/Lightbox';
import PricingBreakdown from '../components/PricingBreakdown';
import DiscountTimer from '../components/DiscountTimer';
import ShareMenu from '../components/ShareMenu';
import { SmartVideoPlayer } from '../components/SharedUI';

const { LuArrowRight, LuCalendar, LuGauge, LuPalette, LuSettings2, LuCheckCircle, LuEye, LuChevronLeft, LuChevronRight, LuZoomIn, LuImages, LuCopy, LuCheck, LuPhoneCall, LuX, LuTicket, LuPlayCircle } = LuIcons;

const getCarUrl = (carId) => {
  // تم إزالة الهاشتاج من هنا
  return `${window.location.origin}/car/${carId}`;
};

const buildWhatsAppMessage = (car) => {
  const hasDiscount = car.discount_enabled && Number(car.discount_price) > 0;
  const displayPrice = hasDiscount ? Number(car.discount_price) : (Number(car.total_price) > 0 ? Number(car.total_price) : (Number(car.price) || 0) + (Number(car.customs_price) || 0) + (Number(car.shipping_price) || 0));
  
  const carUrl = getCarUrl(car.id);
  
  let msg = `🚗 *${car.brand} ${car.model}* - موديل ${car.year}\n`;
  if (hasDiscount) msg += `🏷️ *سعر مخفض:* ${displayPrice.toLocaleString()} دج\n`;
  else msg += `💰 السعر الإجمالي: *${displayPrice.toLocaleString()} دج*\n`;
  
  msg += `🛣️ الممشى: ${Number(car.mileage).toLocaleString()} كم\n`;
  if (car.color) msg += `🎨 اللون: ${car.color}\n`;
  
  msg += `\nإذا كان لديك كود خصم، يمكنك إرساله هنا: \n`;
  msg += `\n🔗 ${carUrl}`;
  return msg;
};

// 🌟 تحديث جذري لمعرض الصور ليدمج الفيديو في المكان الأول
const ImageGallery = ({ images, videoUrl, alt, isSold, onImageClick }) => {
  const [current, setCurrent] = useState(0);

  // بناء مصفوفة الوسائط المدمجة (فيديو أولاً إن وجد، ثم الصور)
  const media = [];
  if (videoUrl) {
    media.push({ type: 'video', url: videoUrl });
  }
  if (images && images.length > 0) {
    images.forEach(img => media.push({ type: 'image', url: img }));
  }

  if (media.length === 0) return (
    <div className="w-full h-64 sm:h-[400px] bg-slate-100 rounded-2xl overflow-hidden">
      <img src="https://images.unsplash.com/photo-1494905998459-36430ba12391?auto=format&fit=crop&q=80&w=800" alt={alt} className="w-full h-full object-cover" />
    </div>
  );

  const currentMedia = media[current];

  return (
    <div className="space-y-3">
      {/* منطقة العرض الرئيسية */}
      <div 
        className={`relative w-full h-64 sm:h-[420px] bg-black rounded-2xl overflow-hidden group ${currentMedia.type === 'image' ? 'cursor-zoom-in' : ''}`}
        onClick={() => {
          // نفتح Lightbox للصور فقط (ونطرح 1 من الاندكس إذا كان هناك فيديو في البداية ليتوافق مع مصفوفة الصور الأصلية)
          if (currentMedia.type === 'image') {
            onImageClick(current - (videoUrl ? 1 : 0));
          }
        }}
      >
        <AnimatePresence mode="wait">
          {currentMedia.type === 'video' ? (
            <motion.div 
              key="video-player"
              className="w-full h-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <SmartVideoPlayer url={currentMedia.url} title={alt} />
            </motion.div>
          ) : (
            <motion.img 
              key={current}
              src={currentMedia.url} 
              alt={`${alt} ${current + 1}`} 
              className={`w-full h-full object-cover ${isSold ? 'grayscale-[40%]' : ''}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onError={e => { e.target.src = 'https://images.unsplash.com/photo-1494905998459-36430ba12391?auto=format&fit=crop&q=80&w=800'; }}
            />
          )}
        </AnimatePresence>

        {currentMedia.type === 'image' && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="bg-black/40 text-white rounded-full p-3 backdrop-blur-sm">
              <SafeIcon icon={LuZoomIn} className="w-6 h-6" />
            </div>
          </div>
        )}

        {media.length > 1 && (
          <>
            <button onClick={e => { e.stopPropagation(); setCurrent(c => c === 0 ? media.length - 1 : c - 1); }} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all z-10">
              <SafeIcon icon={LuChevronRight} className="w-5 h-5" />
            </button>
            <button onClick={e => { e.stopPropagation(); setCurrent(c => c === media.length - 1 ? 0 : c + 1); }} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all z-10">
              <SafeIcon icon={LuChevronLeft} className="w-5 h-5" />
            </button>
            <div className="absolute top-3 left-3 bg-black/50 text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1 backdrop-blur-sm z-10 pointer-events-none">
              <SafeIcon icon={LuImages} className="w-3 h-3" /> {current + 1} / {media.length}
            </div>
          </>
        )}

        {isSold && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="bg-red-600/90 text-white px-8 py-2.5 rounded-full shadow-lg backdrop-blur-sm flex items-center gap-2 rotate-[-5deg]">
              <SafeIcon icon={LuCheckCircle} className="w-5 h-5" />
              <span className="font-black text-xl">تم البيع</span>
            </div>
          </div>
        )}
      </div>

      {/* الشريط السفلي للصور (Thumbnails) */}
      {media.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
          {media.map((item, i) => (
            <button 
              key={i} 
              onClick={() => setCurrent(i)}
              className={`flex-shrink-0 w-20 h-16 rounded-xl overflow-hidden border-2 transition-all relative ${i === current ? 'border-emerald-500 scale-105 shadow-md' : 'border-transparent opacity-60 hover:opacity-100'}`}
            >
              {item.type === 'video' ? (
                <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                  <SafeIcon icon={LuPlayCircle} className="w-8 h-8 text-white/90" />
                </div>
              ) : (
                <img src={item.url} alt="" className="w-full h-full object-cover" onError={e => { e.target.src = 'https://images.unsplash.com/photo-1494905998459-36430ba12391?auto=format&fit=crop&q=80&w=200'; }} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CarDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  // Lead Form State
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', phone: '', coupon: '' });
  const [submittingLead, setSubmittingLead] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);

  useEffect(() => {
    const fetchCar = async () => {
      const { data } = await supabase.from('cars_for_sale').select('*').eq('id', id).single();
      if (data) {
        setCar(data);
        const newCount = (data.views_count || 0) + 1;
        supabase.from('cars_for_sale').update({ views_count: newCount }).eq('id', id).then(() => {});
      }
      setLoading(false);
    };
    fetchCar();
  }, [id]);

  if (loading) return (
    <div className="flex-1 flex items-center justify-center py-20">
      <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!car) return (
    <div className="flex-1 flex flex-col items-center justify-center py-20 text-center px-4">
      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <SafeIcon icon={LuImages} className="w-8 h-8 text-slate-300" />
      </div>
      <h2 className="text-xl font-bold text-slate-800 mb-2">السيارة غير موجودة</h2>
      <p className="text-slate-500 mb-6">ربما تم حذف هذا الإعلان.</p>
      <button onClick={() => navigate('/')} className="flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors">
        <SafeIcon icon={LuArrowRight} className="w-4 h-4" /> العودة للرئيسية
      </button>
    </div>
  );

  const isSold = car.status === 'sold';
  const hasDiscount = car.discount_enabled && Number(car.discount_price) > 0;

  // تجميع الصور (بدون الفيديو، لأن Lightbox مخصص للصور فقط)
  const allImages = [
    ...(car.image_url ? [car.image_url] : []),
    ...(Array.isArray(car.gallery) ? car.gallery.filter(g => g && g !== car.image_url) : []),
  ];

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getCarUrl(car.id));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsApp = () => {
    const msg = buildWhatsAppMessage(car);
    window.open(`https://wa.me/${car.whatsapp}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setSubmittingLead(true);
    try {
      const finalMarketerCode = leadForm.coupon.trim() || null;
      const { error } = await supabase.from('leads').insert([{
        car_id: car.id,
        customer_name: leadForm.name,
        customer_phone: leadForm.phone,
        marketer_code: finalMarketerCode
      }]);
      if (error) throw error;
      
      setLeadSuccess(true);
      setTimeout(() => {
        setLeadModalOpen(false);
        setLeadSuccess(false);
        setLeadForm({ name: '', phone: '', coupon: '' });
      }, 3000);
    } catch (err) {
      alert('حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى.');
    } finally {
      setSubmittingLead(false);
    }
  };

  const stats = [
    { label: 'السنة', value: car.year, icon: LuCalendar, bg: 'bg-blue-100', color: 'text-blue-600' },
    { label: 'الممشى', value: `${Number(car.mileage).toLocaleString()} كم`, icon: LuGauge, bg: 'bg-purple-100', color: 'text-purple-600' },
    ...(car.color ? [{ label: 'اللون', value: car.color, icon: LuPalette, bg: 'bg-rose-100', color: 'text-rose-600' }] : []),
    ...(car.transmission ? [{ label: 'ناقل الحركة', value: car.transmission === 'automatic' ? 'أوتوماتيك' : 'يدوي', icon: LuSettings2, bg: car.transmission === 'automatic' ? 'bg-violet-100' : 'bg-amber-100', color: car.transmission === 'automatic' ? 'text-violet-600' : 'text-amber-600' }] : []),
  ];

  return (
    <div className="flex-1 w-full max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-6">
      <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-medium mb-5 transition-colors group">
        <SafeIcon icon={LuArrowRight} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        العودة للرئيسية
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* القسم الأيمن: معرض الصور والفيديو معاً */}
        <div className="lg:col-span-3">
          <ImageGallery 
            images={allImages} 
            videoUrl={car.video_url} 
            alt={`${car.brand} ${car.model}`}
            isSold={isSold}
            onImageClick={i => { setLightboxIndex(i); setLightboxOpen(true); }}
          />
        </div>

        {/* القسم الأيسر: تفاصيل السيارة والمبيعات */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <div className="flex flex-wrap gap-2 mb-3">
              {!isSold ? 
                <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse inline-block"></span>
                  متوفرة للبيع
                </span> : 
                <span className="bg-red-100 text-red-700 text-xs font-bold px-3 py-1 rounded-full">تم البيع</span>
              }
              {car.condition === 'new' ? 
                <span className="bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full">جديدة</span> : 
                <span className="bg-orange-100 text-orange-700 text-xs font-bold px-3 py-1 rounded-full">مستعملة</span>
              }
            </div>
            
            <h1 className="text-2xl sm:text-4xl font-black text-slate-900 leading-tight">{car.brand} {car.model}</h1>
            <p className="text-slate-400 text-sm mt-2 flex items-center gap-1.5 font-medium">
              <SafeIcon icon={LuEye} className="w-4 h-4" /> {(car.views_count || 0).toLocaleString()} مشاهدة
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {stats.map((s, i) => (
              <div key={i} className="flex items-center gap-3 bg-slate-50 px-4 py-3 rounded-2xl border border-slate-100">
                <div className={`w-8 h-8 ${s.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <SafeIcon icon={s.icon} className={`w-4 h-4 ${s.color}`} />
                </div>
                <div>
                  <p className="text-[11px] text-slate-400 font-bold mb-0.5">{s.label}</p>
                  <p className="text-sm font-black text-slate-700">{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          {hasDiscount && !isSold ? <DiscountTimer car={car} /> : <PricingBreakdown car={car} isSold={isSold} />}

          {car.description && (
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
              <h3 className="text-sm font-black text-slate-800 mb-3">تفاصيل إضافية</h3>
              <p className="text-sm text-slate-600 leading-loose whitespace-pre-line font-medium">{car.description}</p>
            </div>
          )}

          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button 
                onClick={handleWhatsApp}
                disabled={isSold}
                className={`w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-black text-sm transition-all ${isSold ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5'}`}
              >
                {isSold ? <><SafeIcon icon={LuCheckCircle} className="w-5 h-5" /> تم البيع</> : <>
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.845L.057 23.885l6.196-1.448A11.934 11.934 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.894a9.872 9.872 0 01-5.031-1.378l-.361-.214-3.741.981.999-3.648-.235-.374A9.867 9.867 0 012.106 12C2.106 6.58 6.58 2.106 12 2.106S21.894 6.58 21.894 12 17.42 21.894 12 21.894z" />
                  </svg>
                  تواصل عبر واتساب
                </>}
              </button>
              
              <button 
                onClick={() => setLeadModalOpen(true)}
                disabled={isSold}
                className={`w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-black text-sm transition-all ${isSold ? 'hidden' : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5'}`}
              >
                <SafeIcon icon={LuPhoneCall} className="w-5 h-5" />
                طلب اتصال
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <ShareMenu car={car} fullWidth />
              <button 
                onClick={handleCopyLink}
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 text-slate-600 hover:text-blue-700 font-bold text-sm transition-all"
              >
                <SafeIcon icon={copied ? LuCheck : LuCopy} className="w-4 h-4" />
                {copied ? 'تم النسخ!' : 'نسخ الرابط'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {lightboxOpen && allImages.length > 0 && (
        <Lightbox 
          images={allImages} 
          currentIndex={lightboxIndex} 
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setLightboxIndex(i => i === 0 ? allImages.length - 1 : i - 1)}
          onNext={(idx) => {
            if (typeof idx === 'number') setLightboxIndex(idx);
            else setLightboxIndex(i => i === allImages.length - 1 ? 0 : i + 1);
          }}
        />
      )}

      {/* Lead Modal */}
      {leadModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="bg-blue-600 px-6 py-4 flex justify-between items-center text-white">
              <h3 className="font-black text-lg">طلب اتصال بالبائع</h3>
              <button onClick={() => setLeadModalOpen(false)} className="text-blue-100 hover:text-white p-1 rounded-full transition-colors">
                <SafeIcon icon={LuX} className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {leadSuccess ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={LuCheck} className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-800 mb-2">تم استلام طلبك بنجاح!</h4>
                  <p className="text-slate-500">سنتواصل معك في أقرب وقت ممكن لإتمام التفاصيل.</p>
                </div>
              ) : (
                <form onSubmit={handleLeadSubmit} className="space-y-4">
                  <p className="text-sm text-slate-500 mb-4">أدخل بياناتك ليتم التواصل معك بخصوص سيارة <strong className="text-slate-800">{car.brand} {car.model}</strong></p>
                  
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1.5">الاسم الكامل</label>
                    <input 
                      required
                      type="text" 
                      value={leadForm.name}
                      onChange={e => setLeadForm({...leadForm, name: e.target.value})}
                      className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all"
                      placeholder="محمد علي"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1.5">رقم الهاتف</label>
                    <input 
                      required
                      type="text" 
                      value={leadForm.phone}
                      onChange={e => setLeadForm({...leadForm, phone: e.target.value})}
                      dir="ltr"
                      className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none text-right transition-all"
                      placeholder="0555000000"
                    />
                  </div>

                  <div className="pt-3 mt-1 border-t border-slate-100">
                    <label className="block text-sm font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                      <SafeIcon icon={LuTicket} className="w-4 h-4 text-emerald-500" />
                      كود المسوق / كود الخصم (اختياري)
                    </label>
                    <input 
                      type="text" 
                      value={leadForm.coupon}
                      onChange={e => setLeadForm({...leadForm, coupon: e.target.value})}
                      className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 outline-none bg-emerald-50/50 transition-all"
                      placeholder="أدخل الكود إن وجد"
                    />
                  </div>

                  <button 
                    type="submit" 
                    disabled={submittingLead}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-xl transition-all shadow-md hover:shadow-lg mt-4"
                  >
                    {submittingLead ? 'جاري الإرسال...' : 'تأكيد وإرسال الطلب'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarDetail;